import { derived, get, writable } from 'svelte/store';

import type { ApiTypes } from '@api/client';
import {
    login,
    logout,
    signup,
} from '@api/user/auth';
import {
    acceptUserAgreement,
    updateProfile,
    updateSelfInfo,
} from '@api/user/profile';
import {
    resendVerifyEmail,
    updateGender,
    verifyDocuments,
} from '@api/user/signUp';
import {
    CAState,
    Country,
    IdentityStatus,
    PayoutDetailsStatus,
    USState,
} from '@shared/types';

import type { Store, StoreState } from './types';

const initialValues: StoreState = {
    user: undefined,
};

function createStore(): Store {
    const store = writable(initialValues);
    const { subscribe, update, set } = store;

    const updateStore = (data: Partial<StoreState>) => {
        update((state) => ({
            ...state,
            ...data,
        }));
    };

    const updateUser = (user: Partial<StoreState['user']>) => {
        if (user) {
            updateStore({
                user: ({
                    ...get(store).user,
                    ...user,
                } as StoreState['user']),
            });
        } else {
            updateStore({
                user: undefined,
            });

        }
        import('@sentry/sveltekit?inline').then(Sentry => {
            if (user) {
                Sentry.setUser({ id: user.id, username: user.username || user.displayName });
            } else {
                Sentry.setUser(null);
            }
        });

    };

    return {
        getState: () => get(store),
        update,
        set,
        subscribe,
        updateBalance: async (balance: number, bonusBalance: number) => {
            update((state) =>
                state.user
                    ? {
                        user: {
                            ...state.user,
                            operator: {
                                ...(state.user ? state.user.operator : {}),
                                balance,
                                bonusBalance,
                            },
                        } as ApiTypes.UserCurrent,
                    }
                    : state
            );
        },
        signup: async ({ password, email, gender, cookiesAllowedAt, recaptchaToken, recaptchaTokenV2 }) => {
            const { response, data, error } = await signup({
                password,
                gender,
                email,
                cookiesAllowedAt,
                recaptchaToken,
                recaptchaTokenV2,
            });

            if (response.status === 201) {
                updateUser(data);
            }

            if (error) {
                throw new Error((error as Error)?.message, { cause: (error as Error).cause });
            }
        },
        login: async ({
            usernameEmail,
            password,
            isRememberUser,
            recaptchaToken,
            recaptchaTokenV2,
            cookiesAllowedAt,
        }) => {
            const { response, data, error } = await login({
                usernameEmail,
                password,
                isRememberUser,
                recaptchaToken,
                recaptchaTokenV2,
                cookiesAllowedAt,
            });

            if (response.status === 201 && data) {
                const { gtmUserLogin } = await import('$lib/services');

                await gtmUserLogin(data.id);
                updateUser(data);
            }

            if (response.status === 401) {
                throw new Error('Invalid email or password');
            }

            if (error) {
                throw new Error((error as Error)?.message, { cause: (error as Error).cause });
            }
        },
        logout: async () => {
            const { response, error } = await logout();

            if (response.status === 201) {
                updateUser(undefined);
            }

            if (error) {
                throw new Error((error as Error)?.message);
            }
        },
        updateProfile: async ({ location, spokenLanguages, translationLanguage, email, displayName }) => {
            const isUsaOrCanada =
                location &&
                [Country['United States'], Country.Canada].includes(
                    location.country as Country.Canada
                );

            const passRegion =
                isUsaOrCanada &&
                Object.values({ ...USState, ...CAState }).includes(location.region as USState);

            const { response, data, error } = await updateProfile({
                ...(location && {
                    location: {
                        country: location.country || null,
                        region: passRegion ? location.region : null,
                        city: location.city || null,
                    },
                }),
                spokenLanguages,
                translationLanguage,
                email,
                displayName,
            });

            if (response.status === 200) {
                updateUser(data);
            }

            if (error) {
                throw new Error((error as Error)?.message);
            }

            return data;
        },
        updateSignUpGender: async ({ gender, storyId }) => {
            const { response, data, error } = await updateGender({ gender, storyId });

            if (response.status === 201) {
                updateUser(data);
            }

            if (error) {
                throw new Error((error as Error)?.message);
            }
        },
        updateSignUpDocuments: async (files) => {
            const { response, data, error } = await verifyDocuments(files);

            if (response.status === 201) {
                updateUser(data);
            }

            if (error) {
                throw new Error((error as Error)?.message);
            }
        },
        updateSignUpSelfInfo: async ({ spokenLanguages, displayName }) => {
            const { response, data, error } = await updateSelfInfo({ displayName, spokenLanguages });

            if (response.status === 201) {
                updateUser(data);
            }

            if (error) {
                throw new Error((error as Error)?.message);
            }
        },
        acceptUserAgreement: async () => {
            const { response, data, error } = await acceptUserAgreement();

            if (response.status >= 200 && response.status < 300) {
                updateUser(data);
            }

            if (error) {
                throw new Error((error as Error)?.message);
            }
        },
        resendVerifyEmail: async () => {
            const { response, data, error } = await resendVerifyEmail();

            if (response.status >= 200 && response.status < 300) {
                updateUser(data);
            }

            if (error) {
                throw new Error((error as Error)?.message);
            }
        },
        setUser: (data: StoreState['user']) => {
            set({ ...get(store), user: data });
        },
        incrementNextClickCount: () => {
            updateUser({
                nextClickCount: (get(store).user?.nextClickCount || 0) + 1,
            });
        },
        isAuth: (isOperator = null) => {
            const user = get(store);
            return Boolean(user.user && (isOperator !== null ? Boolean(user.user.operator) === isOperator : true));
        },
        resetOperatorPaymentsDetailsStatus: () => {
            updateUser({
                operator: {
                    ...(get(store).user?.operator || {}),
                    payoutDetailsStatus: PayoutDetailsStatus.Pending,
                } as NonNullable<StoreState['user']>['operator'],
            });
        },
        changeDisableSettings(value: boolean) {
            updateUser({
                callsDisabled: value,
            });
        },
    };
}

export const userStore = createStore();

export const isOperator = derived(userStore, ($store) => Boolean($store.user?.operator));
export const isVerifiedOperator = derived(
    userStore,
    ($store) =>
        Boolean($store.user?.operator) && $store.user?.identityStatus === IdentityStatus.Verified
);
